// Mirrored in applications/platform-app/components/web-analytics — change both.
import React, { useEffect, useRef, useState } from 'react';
import { useDash } from '../context';
import { useWebQuery } from '../useWebQuery';
import { useTrack } from './shared';
import { Button, Card, Empty, Note, Switch, Tag, TextInput, Td, Th } from '../ui';
import { errorMessage } from '../api';
import type { SettingsResponse, SettingsUpdate, SiteProtection, SiteSettings } from '../types';
import { ago } from '../format';

/** Defaults mirror WebSiteSchema — used for any field the server leaves out. */
const DEFAULTS: Required<SiteSettings> = { excludeStaff: true, botFilter: true, weeklyReport: true, securityAlerts: true, alertRecipients: [], excludedIps: [] };
const PROTECTION_DEFAULTS: Required<SiteProtection> = { managedRules: true, rateLimits: true };
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Row: React.FC<{ label: string; desc: string; children: React.ReactNode }> = ({ label, desc, children }) => (
  <div className="flex gap-3 items-center py-2.5 border-b border-stone-100">
    <div className="flex-1"><b>{label}</b><div className="text-stone-500 text-xs mt-0.5">{desc}</div></div>
    {children}
  </div>
);

export const SettingsTab: React.FC = () => {
  const d = useDash();
  const admin = d.mode === 'admin';
  const [nonce, setNonce] = useState(0);
  const q = useTrack(useWebQuery<SettingsResponse>({
    api: d.api, siteId: d.siteId, range: { range: '30d', compare: false }, filters: [], want: null, nonce,
    unfiltered: () => d.api.settings(d.siteId),
  }));
  const [local, setLocal] = useState<{ settings: Required<SiteSettings>; protection: Required<SiteProtection> } | null>(null);
  const [adding, setAdding] = useState<string | null>(null);
  const [addErr, setAddErr] = useState(false);
  const queue = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    const site = q.data?.site;
    if (!site) { setLocal(null); return; }
    setLocal({
      settings: { ...DEFAULTS, ...(site.settings || {}) } as Required<SiteSettings>,
      // Admin on one site edits that site's own record; everywhere else the effective (platform) value shows.
      protection: { ...PROTECTION_DEFAULTS, ...(site.protection || {}), ...(admin ? q.data?.siteProtection || {} : {}) } as Required<SiteProtection>,
    });
  }, [q.data, admin]);

  if (q.error) {
    const missing = q.errorStatus === 404 || /not found/i.test(q.error);
    return <Card><Empty>{missing ? 'Settings are not available for this website yet.' : q.error}</Empty></Card>;
  }

  // Saves run one at a time, in order; a failure reverts ONLY its own key (and only if
  // nothing later changed that key), so a failed save never undoes a later toggle.
  type Group = 'settings' | 'protection';
  const save = (group: Group, key: string, value: unknown) => {
    if (!local) return;
    const prev = (local[group] as Record<string, unknown>)[key];
    const put = (l: NonNullable<typeof local>, v: unknown) => ({ ...l, [group]: { ...l[group], [key]: v } });
    setLocal((l) => (l ? put(l, value) : l));
    const body: SettingsUpdate = group === 'protection' ? { protection: { [key]: value } } : { [key]: value };
    queue.current = queue.current.then(async () => {
      try {
        await d.api.saveSettings(d.siteId, body);
        d.toast('Saved');
      } catch (e) {
        setLocal((l) => (l && (l[group] as Record<string, unknown>)[key] === value ? put(l, prev) : l));
        d.toast(errorMessage(e));
      }
    });
  };
  const setting = (k: keyof SiteSettings) => (v: boolean) => save('settings', k, v);
  const protect = (k: keyof SiteProtection) => (v: boolean) => save('protection', k, v);

  const recipients = local?.settings.alertRecipients || [];
  const addRecipient = () => {
    const e = (adding || '').trim().toLowerCase();
    if (!EMAIL.test(e)) { setAddErr(true); return; }
    setAdding(null); setAddErr(false);
    if (recipients.includes(e)) return;
    const next = [...recipients, e].slice(0, 10);
    save('settings', 'alertRecipients', next);
  };
  const removeRecipient = (e: string) => {
    const next = recipients.filter((x) => x !== e);
    save('settings', 'alertRecipients', next);
  };

  const tracking = q.data?.tracking || (q.data?.site?.hostnames || []).map((h) => ({ hostname: h, status: 'no_data' as const, lastEventAt: q.data?.site?.lastEventAt?.[h] }));
  // On `_all` only weeklyReport / alertRecipients / protection are platform-wide; the rest are set per website.
  const PER_SITE = ['excludeStaff', 'botFilter', 'securityAlerts'];
  const tg = (on: boolean | undefined, onChange: (v: boolean) => void, id: string) => (
    d.siteId === '_all' && PER_SITE.includes(id)
      ? <Tag tone="gray">Set per website</Tag>
      : <Switch on={!!on} onChange={onChange} disabled={!local} testId={`wa-set-${id}`} />
  );

  return (
    <div className="grid gap-3.5 grid-cols-1 md:grid-cols-2">
      <Card title="Tracking status">
        {!tracking.length ? <Empty>{d.siteId === '_all' ? 'Every website RubyTill hosts reports here — pick a website to see its domains.' : 'No domains yet.'}</Empty> : (
          <table className="w-full border-collapse text-[13px]">
            <thead><tr><Th>Domain</Th><Th>Status</Th><Th>Last event</Th></tr></thead>
            <tbody>
              {tracking.map((t) => (
                <tr key={t.hostname}>
                  <Td><b>{t.hostname}</b></Td>
                  <Td>{t.status === 'receiving' ? <Tag tone="green">● Receiving data</Tag> : <Tag tone="gray">No data in 24 h</Tag>}</Td>
                  <Td className="text-stone-500">{ago(t.lastEventAt)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Note>Tracking is built into every website RubyTill hosts. There's nothing to install. It uses no cookies, so no cookie banner is needed for analytics.</Note>
      </Card>

      <Card title="Data">
        <Row label="Exclude staff traffic" desc="Visits from logged-in staff and your own POS devices are not counted.">{tg(local?.settings.excludeStaff, setting('excludeStaff'), 'excludeStaff')}</Row>
        <Row label="Filter bots from reports" desc="Bots still appear on the Security tab.">{tg(local?.settings.botFilter, setting('botFilter'), 'botFilter')}</Row>
        <Row label="Weekly email report" desc="Every Monday: traffic, sales from your website and security summary.">{tg(local?.settings.weeklyReport, setting('weeklyReport'), 'weeklyReport')}</Row>
        <div className="py-2.5"><b>Data kept</b><div className="text-stone-500 text-xs mt-0.5">Daily totals: forever · Visit-level detail: 13 months · Security log: 90 days</div></div>
      </Card>

      <Card title="Protection">
        <Row label="Block known bad bots and attack patterns" desc="Common attacks, Tor and exploit scanners, fake browsers.">
          {admin ? tg(local?.protection.managedRules, protect('managedRules'), 'managedRules') : <Tag tone="gray">Managed by RubyTill</Tag>}
        </Row>
        <Row label="Rate limit logins and checkout" desc="Slows down password guessing and card testing.">
          {admin ? tg(local?.protection.rateLimits, protect('rateLimits'), 'rateLimits') : <Tag tone="gray">Managed by RubyTill</Tag>}
        </Row>
        <Row label="Security alerts by email" desc="Sent when the threat level reaches Elevated or High, or when card testing is detected.">
          {tg(local?.settings.securityAlerts, setting('securityAlerts'), 'securityAlerts')}
        </Row>
        {admin && <Note>Changes here apply to {d.siteId === '_all' ? <b>every website as the platform default.</b> : <><b>{d.site?.name || d.siteId}</b> only, overriding the platform default.</>}</Note>}
      </Card>

      <Card title="Alert recipients">
        <div className="flex gap-1.5 flex-wrap items-center">
          {recipients.map((e) => (
            <span key={e} className="inline-flex items-center gap-1.5 rounded-full pl-3 pr-1 h-8 text-xs font-semibold" style={{ background: '#FFF1F3', color: '#9F1239', border: '1px solid #fecdd3' }}>
              {e}
              <button type="button" aria-label={`Remove ${e}`} onClick={() => removeRecipient(e)} className="relative w-[22px] h-[22px] rounded-full border-0 before:content-[''] before:absolute before:-inset-[11px]" style={{ background: '#fecdd3', color: '#9F1239' }}>×</button>
            </span>
          ))}
          {adding === null
            ? <Button size="sm" disabled={!local || recipients.length >= 10} onClick={() => setAdding('')}>+ Add</Button>
            : (
              <span className="flex gap-1.5 items-center">
                <TextInput
                  autoFocus value={adding} invalid={addErr} placeholder="name@example.com" className="!w-56"
                  onChange={(e) => { setAdding(e.target.value); setAddErr(false); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') addRecipient(); if (e.key === 'Escape') setAdding(null); }}
                />
                <Button tone="ruby" size="sm" onClick={addRecipient}>Add</Button>
              </span>
            )}
          {!recipients.length && adding === null && <span className="text-xs text-stone-500">No extra recipients.</span>}
        </div>
        <Note>{admin ? 'Super Admin is also alerted when any tenant website reaches High.' : 'RubyTill security is also alerted for High-severity events on your website.'}</Note>
      </Card>
    </div>
  );
};

export default SettingsTab;
